import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import { authService } from 'src/services/auth';
import { LoginRequest, UserResponse } from 'src/services/interfaces/auth';
import { AxiosError } from 'src/services/interfaces/axios';
import { applicationService } from 'src/services/application';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { clearOnboardingContactData } from '@utils/clearOnboardingContact';
import { useApplicationStore } from './application';

interface User extends UserResponse {
  username?: string;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  wasAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  success: string | null;
  setToken: (token: string) => void;
  setUserFromGetMe: (user: User) => void;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (params: {
    token: string;
    password: string;
    rpt: string;
  }) => Promise<void>;
  register: (credentials: LoginRequest) => Promise<void>;
  clear: () => void;
}

// Функция-обёртка для обработки ошибок Axios
const handleAxiosError = (error: AxiosError | unknown, defaultMessage: string): string => {
  const axiosError = error as AxiosError;
  if (axiosError?.response?.data) {
    const responseData = axiosError.response.data as any;
    if (responseData.errorCode === 'userEmailAlreadyExists') {
      return 'An account with this email already exists. Please log in instead.';
    }
    if (responseData.errors?.length > 0) {
      const firstError = responseData.errors[0];
      if (typeof firstError === 'string') {
        return firstError;
      }
      if (typeof firstError === 'object' && firstError !== null) {
        if (firstError.message) return firstError.message;
        if (Array.isArray(firstError.errors) && firstError.errors.length > 0) {
          return firstError.errors[0];
        }
        return JSON.stringify(firstError);
      }
    }
    if (responseData.message) {
      return responseData.message;
    }
  }
  return defaultMessage;
};

const useAuthStore = create<AuthState>((set) => {
  let initialToken: string | null = null;
  let initialUser: User | null = null;
  let isAuthenticated = false;
  let wasAuthenticated = false;

  if (typeof window !== 'undefined') {
    initialToken = localStorage.getItem('token');
    if (initialToken) {
      try {
        initialUser = jwtDecode<User>(initialToken);
        isAuthenticated = true;
        wasAuthenticated = true;
      } catch (error) {
        console.error('Invalid token:', error);
        localStorage.removeItem('token');
        initialToken = null;
      }
    }
  }

  return {
    token: initialToken,
    user: initialUser,
    isAuthenticated,
    wasAuthenticated,
    isLoading: false,
    error: null,
    success: null,

    clear: () => set({ error: null, success: null }),

    setToken: (token: string) => {
      try {
        const decodedUser = jwtDecode<User>(token);
        localStorage.setItem('token', token);
        set({
          token,
          user: decodedUser,
          isAuthenticated: true,
          wasAuthenticated: true,
          error: null,
          success: null,
        });
      } catch (error) {
        console.error('Failed to decode token:', error);
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          wasAuthenticated: false,
          error: 'Failed to decode token.',
        });
        localStorage.removeItem('token');
      }
    },

    setUserFromGetMe: (user: User) => {
      set({
        user,
        isAuthenticated: true,
        wasAuthenticated: true,
        error: null,
        success: null,
      });
    },

    login: async (credentials: LoginRequest) => {
      set({ isLoading: true, error: null, success: null });
      try {
        const { accessToken } = await authService.login(credentials);
        localStorage.setItem('token', accessToken);
        // Drop any cached contact data from a previous user session.
        clearOnboardingContactData();

        const userResponse = await authService.getMe();
        let isMainPlanRejected = false;
        try {
          const applicationState = await applicationService.applicationState();
          if (applicationState.phqRejectedAt) {
            isMainPlanRejected = true;
          }
        } catch (error) {
          console.log('Application state not available yet:', error);
        }
        console.log('isMainPlanRejected login', isMainPlanRejected);
        useApplicationStore
          .getState()
          .setIsMainPlanRejected(isMainPlanRejected);
        useApplicationStore.getState().fetchActiveSubscriptionPlans();
        const user = {
          ...userResponse,
          id: userResponse.id,
          username: userResponse.email,
        };
        // window.location.reload();
        set({
          token: accessToken,
          user,
          isAuthenticated: true,
          wasAuthenticated: true,
          isLoading: false,
          success: 'Login successful.',
        });
      } catch (error: AxiosError | unknown) {
        set({
          isLoading: false,
          error: handleAxiosError(error, 'Login failed.'),
        });
      }
    },

    logout: () => {
      localStorage.removeItem('token');
      clearOnboardingContactData();
      set({
        token: null,
        user: null,
        isAuthenticated: false,
        wasAuthenticated: true,
        error: null,
        success: null,
      });
    },

    forgotPassword: async (email: string) => {
      set({ isLoading: true, error: null, success: null });
      try {
        await authService.forgotPassword(email);
        set({
          isLoading: false,
          success: 'Password reset link sent to your email.',
        });
      } catch (error: AxiosError | unknown) {
        set({
          isLoading: false,
          error: handleAxiosError(error, 'Failed to send password reset link.'),
        });
      }
    },

    resetPassword: async ({ token, password, rpt }) => {
      set({ isLoading: true, error: null, success: null });
      try {
        await authService.resetPassword({ token, password, rpt });
        set({
          isLoading: false,
          success: 'Password has been reset successfully.',
        });
      } catch (error: AxiosError | unknown) {
        set({
          isLoading: false,
          error: handleAxiosError(error, 'Failed to reset password.'),
        });
      }
    },

    register: async (credentials: LoginRequest) => {
      set({ isLoading: true, error: null, success: null });
      try {
        const { accessToken } = await authService.register(credentials);
        localStorage.setItem('token', accessToken);
        // Fresh account -> drop any cached contact data from a previous session.
        clearOnboardingContactData();

        const userResponse = await authService.getMe();
        const user = {
          ...userResponse,
          id: userResponse.id,
          username: userResponse.email,
        };

        let isMainPlanRejected = false;
        try {
          const applicationState = await applicationService.applicationState();
          if (applicationState.phqRejectedAt) {
            isMainPlanRejected = true;
          }
        } catch (error) {
          console.log('Application state not available yet:', error);
        }
        console.log('isMainPlanRejected register', isMainPlanRejected);
        useApplicationStore
          .getState()
          .setIsMainPlanRejected(isMainPlanRejected);
        useApplicationStore.getState().fetchActiveSubscriptionPlans();

        set({
          token: accessToken,
          user,
          isAuthenticated: true,
          wasAuthenticated: true,
          isLoading: false,
          success: 'Registration successful.',
        });

        try {
          const onboardingStore = useOnboardingStore.getState();
          onboardingStore.setData({
            applicants: [
              {
                applicant: 'primary',
                firstName: credentials.firstName || '',
                lastName: credentials.lastName || '',
                midName: '',
                dateOfBirth: {
                  month: '',
                  day: '',
                  year: '',
                },
                height: {
                  ft: '',
                  in: '',
                },
                weight: '',
                nicotine: false,
              } as any,
            ],
          });
        } catch (error) {
          console.log('Failed to fill onboarding data:', error);
        }
      } catch (error: AxiosError | unknown) {
        set({
          isLoading: false,
          error: handleAxiosError(error, 'Registration failed.'),
        });
      }
    },
  };
});

export { useAuthStore };
